import _ from 'lodash';
import moment from 'moment-timezone';
import { sqDurationStore, sqTrendConditionStore, sqTrendSeriesStore } from '@/core/core.stores';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import {
  DEFAULT_ANOMALY_THRESHOLD,
  DEFAULT_MONITORING_CONTRAST,
  DEFAULT_NUM_COMPONENTS,
  INPUT_NAMES,
} from '@/tools/principalComponentAnalysisV2/principalComponentAnalysis.constants';
import type {
  BaseToolPersistedState,
  ModelRehydratePayload,
  ToolConfigParams,
  ToolItem,
  ToolParameter,
} from '@/toolSelection/baseTool.store';
import type {
  OutputSignalType,
  OutputType,
} from '@/tools/principalComponentAnalysisV2/principalComponentAnalysis.constants';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';
import type { Capsule } from '@/utilities/datetime.constants';

export const DEFAULT_DURATION = { value: 0, units: 'min' };

interface PrincipalComponentAnalysisStateV2 extends BaseToolPersistedState {
  windowStart: number;
  windowEnd: number;
  window: any; // any because of Baobab's `monkey()`
  modelPropertiesExpanded: boolean;
  numComponents: number;
  anomalyThreshold: number;
  monitoringContrast: number;
  cleanCapsules: boolean;
  capsuleMinDuration: ValueWithUnitsItem;
  capsuleMergeDuration: ValueWithUnitsItem;
  inputSignals: ToolItem[];
  condition?: ToolItem;
  restrictToCondition?: ToolItem;
}

export type PrincipalComponentAnalysisDehydratedStateV2 = Omit<PrincipalComponentAnalysisStateV2, 'window'>;

export class PrincipalComponentAnalysisStoreV2 extends BaseToolStore {
  static readonly storeName = 'sqPrincipalComponentAnalysisStoreV2';
  type = TREND_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V_2;
  parameterDefinitions = {
    condition: {
      predicate: ['name', 'condition'],
    },
    restrictToCondition: {
      predicate: ['name', 'restrictToCondition'],
    },
    inputSignals: {
      predicate: (parameter: ToolParameter) => parameter?.name?.startsWith(INPUT_NAMES.signalInput),
      multiple: true,
    },
  };

  initialize(): void {
    const initialState: PrincipalComponentAnalysisStateV2 = {
      ...BASE_TOOL_COMMON_PROPS,
      windowStart: sqDurationStore.displayRange.start.valueOf(),
      windowEnd: sqDurationStore.displayRange.end.valueOf(),
      window: this.monkey(['windowStart'], ['windowEnd'], (startTime, endTime) => ({ startTime, endTime })),
      modelPropertiesExpanded: false,
      numComponents: DEFAULT_NUM_COMPONENTS,
      anomalyThreshold: DEFAULT_ANOMALY_THRESHOLD,
      monitoringContrast: DEFAULT_MONITORING_CONTRAST,
      cleanCapsules: false,
      capsuleMinDuration: DEFAULT_DURATION,
      capsuleMergeDuration: DEFAULT_DURATION,
      condition: undefined,
      restrictToCondition: undefined,
      inputSignals: [],
    };
    this.state = this.immutable(initialState);
  }

  get inputSignals(): ToolItem[] {
    return this.state.get('inputSignals');
  }

  get condition(): ToolItem | undefined {
    return this.state.get('condition');
  }

  get restrictToCondition(): ToolItem | undefined {
    return this.state.get('restrictToCondition');
  }

  get window(): { startTime: number; endTime: number } {
    return this.state.get('window');
  }

  get modelPropertiesExpanded(): boolean {
    return this.state.get('modelPropertiesExpanded');
  }

  get numComponents(): number {
    return this.state.get('numComponents');
  }

  get anomalyThreshold(): number {
    return this.state.get('anomalyThreshold');
  }

  get monitoringContrast(): number {
    return this.state.get('monitoringContrast');
  }

  get outputType(): OutputType {
    return this.state.get('outputType');
  }

  get outputSignalType(): OutputSignalType {
    return this.state.get('outputSignalType');
  }

  get cleanCapsules(): boolean {
    return this.state.get('cleanCapsules');
  }

  get capsuleMinDuration(): ValueWithUnitsItem {
    return this.state.get('capsuleMinDuration');
  }

  get capsuleMergeDuration(): ValueWithUnitsItem {
    return this.state.get('capsuleMergeDuration');
  }

  dehydrate(): PrincipalComponentAnalysisDehydratedStateV2 {
    return this.state.serialize();
  }

  rehydrate(dehydratedState: Partial<PrincipalComponentAnalysisDehydratedStateV2>): void {
    this.state.merge(dehydratedState);
  }

  setTrainingWindow(payload: { window: Capsule }): void {
    this.state.set('windowStart', _.get(payload.window, 'startTime'));
    this.state.set('windowEnd', _.get(payload.window, 'endTime'));
  }

  setModelPropertiesExpanded(payload: { modelPropertiesExpanded: boolean }) {
    this.state.set('modelPropertiesExpanded', payload.modelPropertiesExpanded);
  }

  setNumComponents(payload: { numComponents: number }): void {
    this.state.set('numComponents', payload.numComponents);
  }

  setAnomalyThreshold(payload: { anomalyThreshold: number }): void {
    this.state.set('anomalyThreshold', payload.anomalyThreshold);
  }

  setMonitoringContrast(payload: { monitoringContrast: number }): void {
    this.state.set('monitoringContrast', payload.monitoringContrast);
  }

  setCleanCapsules(payload: { cleanCapsules: boolean }): void {
    this.state.set('cleanCapsules', payload.cleanCapsules);
  }

  setCapsuleMinDuration(payload: { capsuleMinDuration: ValueWithUnitsItem }): void {
    this.state.set('capsuleMinDuration', payload.capsuleMinDuration);
  }

  setCapsuleMergeDuration(payload: { capsuleMergeDuration: ValueWithUnitsItem }): void {
    this.state.set('capsuleMergeDuration', payload.capsuleMergeDuration);
  }

  override modifyConfigParams<TConfig extends ToolConfigParams>(config: TConfig): TConfig {
    return _.omit(config, ['modelPropertiesExpanded']) as TConfig;
  }

  modelToolRehydrateForEdit = (payload: ModelRehydratePayload) => {
    if (payload.type !== this.type) {
      return;
    }
    this.initialize();
    this.state.set('id', payload.instanceId);
    (payload.modelData?.inputFeatures ?? []).forEach((feature: any) => {
      const inputName: string = feature.name;
      switch (inputName) {
        case SeeqNames.Models.InstanceNameInputFeatureName:
          // TODO: CRAB-53864: Handle duplicate names more gracefully
          this.state.set('name', payload.instanceId ? feature.inputText : `${feature.inputText} (Copy)`);
          break;
        case INPUT_NAMES.anomalyThresholdInput:
          this.state.set('anomalyThreshold', feature.inputNumeric ?? DEFAULT_ANOMALY_THRESHOLD);
          break;
        case INPUT_NAMES.nComponentsInput:
          this.state.set('numComponents', feature.inputNumeric ?? DEFAULT_NUM_COMPONENTS);
          break;
        case INPUT_NAMES.monitoringSignalContrastInput:
          this.state.set('monitoringContrast', feature.inputNumeric ?? DEFAULT_MONITORING_CONTRAST);
          break;
        case INPUT_NAMES.startTimeInput: {
          const startTimeInput = JSON.parse(feature.inputText);
          this.state.set('windowStart', moment(startTimeInput).valueOf());
          break;
        }
        case INPUT_NAMES.endTimeInput: {
          const endTimeInput = JSON.parse(feature.inputText);
          this.state.set('windowEnd', moment(endTimeInput).valueOf());
          break;
        }
        case INPUT_NAMES.mergeCapsuleDurationInput: {
          this.state.set('cleanCapsules', !_.isNil(feature.inputNumeric));
          const mergeDuration: ValueWithUnitsItem = {
            value: Number(feature.inputNumeric ?? DEFAULT_DURATION.value),
            units: feature.inputNumericUom ?? DEFAULT_DURATION.units,
          };
          this.state.set('capsuleMergeDuration', mergeDuration);
          break;
        }
        case INPUT_NAMES.minCapsuleDurationInput: {
          this.state.set('cleanCapsules', !_.isNil(feature.inputNumeric));
          const mergeDuration: ValueWithUnitsItem = {
            value: Number(feature.inputNumeric ?? DEFAULT_DURATION.value),
            units: feature.inputNumericUom ?? DEFAULT_DURATION.units,
          };
          this.state.set('capsuleMinDuration', mergeDuration);
          break;
        }
        case INPUT_NAMES.trainWithinConditionInput:
          if (feature.inputItem) {
            const item = sqTrendConditionStore.findItem(feature.inputItem) || { id: feature.inputItem };
            this.state.set('condition', _.pick(item, ['id', 'name']));
          }
          break;

        case INPUT_NAMES.restrictOutputToConditionInput: {
          if (feature.inputItem) {
            const item = sqTrendConditionStore.findItem(feature.inputItem) || { id: feature.inputItem };
            this.state.set('restrictToCondition', _.pick(item, ['id', 'name']));
          }
          break;
        }
        default:
          if (inputName.startsWith(INPUT_NAMES.signalInput) && feature.inputItem) {
            const existingSignals = this.state.get('inputSignals') || [];
            const item = sqTrendSeriesStore.findItem(feature.inputItem) || { id: feature.inputItem };
            this.state.set('inputSignals', [...existingSignals, _.pick(item, ['id', 'name'])]);
          }
          break;
      }
    });
  };

  protected readonly handlers = {
    ...this.baseHandlers,
    PCA_SET_TRAINING_WINDOW: this.setTrainingWindow,
    PCA_SET_MODEL_PROPERTIES_EXPANDED: this.setModelPropertiesExpanded,
    PCA_SET_NUM_COMPONENTS: this.setNumComponents,
    PCA_SET_ANOMALY_THRESHOLD: this.setAnomalyThreshold,
    PCA_SET_MONITORING_CONTRAST: this.setMonitoringContrast,
    PCA_SET_CLEAN_CAPSULES: this.setCleanCapsules,
    PCA_SET_CAPSULE_MIN_DURATION: this.setCapsuleMinDuration,
    PCA_SET_CAPSULE_MERGE_DURATION: this.setCapsuleMergeDuration,
    MODEL_TOOL_REHYDRATE_FOR_EDIT: this.modelToolRehydrateForEdit,
  };
}
